import { makeQueryOptions } from '@mint-lib/api-zod-support/react-query';

import { defaultApi, defaultClient } from '@mint-sdk/api-core';
import { z } from 'zod';

import {
  GA4IntegrationCreateRequestSchema,
  GA4IntegrationCreateResponseSchema,
  GA4ExternalAccountSchema,
  GA4ExternalAccountsQuerySchema,
  GA4ExternalAccountsResponseSchema,
  GA4IntegrationCreateV3RequestSchema,
  GA4IntegrationOauthCallbackQuerySchema,
  GA4IntegrationOauthCallbackResponseSchema,
  GA4IntegrationOauthCreateRequestSchema,
  GA4IntegrationOauthUpdateResponseSchema,
  GA4IntegrationOauthUrlQuerySchemaLegacy,
  GA4IntegrationOauthUrlRequestSchema,
  GA4IntegrationOauthUrlResponseSchema,
  GA4IntegrationOauthUrlResponseSchemaLegacy,
  GA4IntegrationUpdateRequestSchema,
  GA4IntegrationUpdateV3RequestSchema,
  GA4IntegrationUpdateV3ResponseSchema,
  GoogleAnalyticsIntegrationSchema,
  GoogleAnalyticsResponseSchema,
} from '../shared/schemas/GoogleAnalyticsSchema.js';

export const getGoogleAnalyticsIntegrations = defaultApi.declareEndpoint({
  url: 'ga4-service/integrations/?add_is_refresh_token_valid_flag=true' as const,
  method: 'GET',
  responseSchema: GoogleAnalyticsResponseSchema,
});

export const getQueryOptions = makeQueryOptions(
  getGoogleAnalyticsIntegrations,
  {
    processResponse(data) {
      return data?.data;
    },
  },
);

export const getGA4Integrations = defaultClient.queryFromEndpoint(
  getGoogleAnalyticsIntegrations,
  {
    processResponse(data) {
      return data?.data?.items;
    },
  },
);

export const getGA4IntegrationOauthUpdate = defaultApi.declareEndpoint({
  url: 'ga4-service/integrations/oauth_update_integration/$integrationId' as const,
  method: 'GET',
  responseSchema: GA4IntegrationOauthUpdateResponseSchema,
});

export const getOauthQueryUpdateOptions = makeQueryOptions(
  getGA4IntegrationOauthUpdate,
  {
    processResponse(data) {
      return data?.data.items;
    },
  },
);

export const createGA4IntegrationOauthUrl = defaultApi.declareEndpoint({
  url: 'ga4-service/integrations/oauth_create_url/' as const,
  method: 'POST',
  requestSchema: GA4IntegrationOauthCreateRequestSchema,
  responseSchema: GA4IntegrationOauthUpdateResponseSchema,
});

export const postGA4IntegrationOauthUrl = defaultClient.mutationFromEndpoint(
  createGA4IntegrationOauthUrl,
  {
    processResponse(data) {
      return data?.data?.items[0].url;
    },
  },
);

export const createGA4Integration = defaultApi.declareEndpoint({
  url: 'ga4-service/integrations/oauth_callback/' as const,
  method: 'POST',
  requestSchema: GA4IntegrationCreateRequestSchema,
  responseSchema: GA4IntegrationCreateResponseSchema,
});

// TODO(ga4BackendOwnedAuth): delete along with the flag, together with
// getGA4OauthUrlLegacy, postGA4Integration (no "Legacy" suffix!) and the
// legacy client-owned GA4 Mint 2.0 flow that calls them.
export const postGA4OAuthCallback = defaultClient.mutation({
  url: 'ga4-service/v3/integrations/oauth-callback/' as const,
  method: 'POST',
  requestSchema: z.object({}),
  snakeCaseQueryParams: true,
  querySchema: GA4IntegrationOauthCallbackQuerySchema,
  responseSchema: GA4IntegrationOauthCallbackResponseSchema,
  processResponse(data) {
    return data.data?.items[0];
  },
});

// TODO(ga4BackendOwnedAuth): delete along with the flag.
export const getGA4OauthUrlLegacy = defaultClient.query({
  url: 'ga4-service/v3/integrations/oauth-url/' as const,
  method: 'GET',
  querySchema: GA4IntegrationOauthUrlQuerySchemaLegacy,
  clientOptions: {
    request: {
      snakeCaseQueryParams: true,
    },
  },
  responseSchema: GA4IntegrationOauthUrlResponseSchemaLegacy,
  processResponse(data) {
    return data.data?.items[0];
  },
});

export const getGA4OauthUrl = defaultClient.mutation({
  url: 'ga4-service/v3/integrations/oauth-url/' as const,
  method: 'POST',
  requestSchema: GA4IntegrationOauthUrlRequestSchema,
  responseSchema: GA4IntegrationOauthUrlResponseSchema,
  processResponse(data) {
    return data.data?.items[0];
  },
});

export const getGA4ExternalAccounts = defaultClient.query({
  url: 'ga4-service/v3/external-data/accounts/' as const,
  method: 'GET',
  querySchema: GA4ExternalAccountsQuerySchema,
  responseSchema: GA4ExternalAccountsResponseSchema,
  clientOptions: {
    request: {
      snakeCaseQueryParams: true,
    },
  },
  processResponse(data) {
    return data.data?.items;
  },
});

export const patchGA4IntegrationV3 = defaultClient.mutation({
  url: 'ga4-service/v3/integrations/$integrationId/' as const,
  method: 'PATCH',
  requestSchema: GA4IntegrationUpdateV3RequestSchema,
  responseSchema: GA4IntegrationUpdateV3ResponseSchema,
  processResponse(data) {
    return data.data?.items[0];
  },
});

// TODO(ga4BackendOwnedAuth): delete along with the flag. No "Legacy"
// suffix here - don't miss it during cleanup.
export const postGA4Integration = defaultClient.mutation({
  url: 'ga4-service/v3/integrations/' as const,
  method: 'POST',
  requestSchema: GA4IntegrationCreateV3RequestSchema,
  responseSchema: GA4IntegrationCreateResponseSchema,
  processResponse(data) {
    return data.data?.items[0];
  },
});

export const deleteGA4Integration = defaultApi.declareEndpoint({
  url: 'ga4-service/integrations/$integrationId' as const,
  method: 'DELETE',
  responseSchema: z.object({}),
});

export const deleteGA4IntegrationMutation = defaultClient.mutation({
  url: 'ga4-service/integrations/$integrationId' as const,
  method: 'DELETE',
  responseSchema: z.object({}),
  processResponse() {
    return true;
  },
});

export const updateGA4Integration = defaultApi.declareEndpoint({
  url: 'ga4-service/integrations/$integrationId' as const,
  method: 'PATCH',
  requestSchema: GA4IntegrationUpdateRequestSchema,
  responseSchema: GA4IntegrationCreateResponseSchema,
});

export const patchGA4Integration = defaultClient.mutationFromEndpoint(
  updateGA4Integration,
  {
    processResponse(data) {
      return data.data?.items[0];
    },
  },
);

export type GoogleAnalyticsIntegrationType = z.infer<
  typeof GoogleAnalyticsIntegrationSchema
>;

export type GA4IntegrationCreateRequestType = z.infer<
  typeof GA4IntegrationCreateRequestSchema
>;

export type GA4ExternalAccountType = z.infer<typeof GA4ExternalAccountSchema>;

export type GA4IntegrationOauthCreateRequestType = z.infer<
  typeof GA4IntegrationOauthCreateRequestSchema
>;
